import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Loader2, AlertCircle } from "lucide-react";
import GoldSeam from "@/components/GoldSeam";
import { api } from "@/lib/api";

const MAX_TRIES = 12;

const fmt = (iso) =>
  new Date(iso).toLocaleString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Asia/Kolkata",
  });

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(sessionId ? "checking" : "missing");
  const [booking, setBooking] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) return undefined;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setBooking(data);
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("failed");
          return;
        }
      } catch {
        setState("failed");
        return;
      }
      tries.current += 1;
      if (tries.current >= MAX_TRIES) {
        setState("pending");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <main className="pt-20" data-testid="payment-success-page">
      <section className="py-24 md:py-36">
        <div className="max-w-2xl mx-auto px-6 text-center">
          {state === "checking" && (
            <div data-testid="payment-checking">
              <Loader2 size={26} className="mx-auto text-gold animate-spin" />
              <h1 className="mt-8 font-display text-ink text-3xl md:text-4xl">Confirming your payment…</h1>
              <p className="mt-4 text-inkdim text-sm">This takes a moment. Please don't close the page.</p>
            </div>
          )}

          {state === "paid" && (
            <div data-testid="payment-confirmed">
              <span className="inline-flex w-14 h-14 rounded-full border border-gold items-center justify-center text-gold">
                <Check size={22} strokeWidth={1.5} />
              </span>
              <h1 className="mt-8 font-display text-ink font-medium text-4xl md:text-5xl leading-[1.1]">
                Your session is confirmed.
              </h1>
              {booking?.slot_start && (
                <p className="mt-6 font-display italic text-goldink text-xl md:text-2xl" data-testid="payment-slot">
                  {fmt(booking.slot_start)} IST
                </p>
              )}
              <p className="mt-6 text-inkdim text-[15px] leading-relaxed">
                A confirmation is on its way to your inbox. Rajdeep will send the private
                joining link before the session — nothing else is needed from you.
              </p>
              <GoldSeam variant="horizontal" className="mx-auto mt-12 w-56 h-10 opacity-70" delay={0.2} />
              <Link
                to="/"
                data-testid="payment-home-link"
                className="inline-block mt-10 bg-deep text-canvas text-xs uppercase tracking-[0.24em] font-semibold px-9 py-4 hover:bg-gold transition-colors duration-300"
              >
                Back to the home page
              </Link>
            </div>
          )}

          {(state === "pending" || state === "failed" || state === "missing") && (
            <div data-testid="payment-unresolved">
              <span className="inline-flex w-14 h-14 rounded-full border border-gold items-center justify-center text-gold">
                <AlertCircle size={22} strokeWidth={1.5} />
              </span>
              <h1 className="mt-8 font-display text-ink font-medium text-3xl md:text-4xl leading-[1.15]">
                {state === "pending" ? "Still waiting on the bank" : "We couldn't confirm this payment"}
              </h1>
              <p className="mt-6 text-inkdim text-[15px] leading-relaxed">
                {state === "pending"
                  ? "Your payment is processing. If it clears, the confirmation email will arrive shortly — no need to pay again."
                  : "Nothing has been charged twice. Please try booking again, or message Rajdeep and she'll sort it out personally."}
              </p>
              <Link
                to="/booking"
                data-testid="payment-retry-link"
                className="inline-block mt-10 bg-deep text-canvas text-xs uppercase tracking-[0.24em] font-semibold px-9 py-4 hover:bg-gold transition-colors duration-300"
              >
                Back to booking
              </Link>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
